import React, { useEffect } from 'react';
import {
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerHeader,
  DrawerOverlay,
  Button,
  Text,
  Input,
  Select,
  useToast,
  VStack,
  HStack,
  SimpleGrid,
  useColorModeValue,
  Box,
  List,
  ListItem,
  Collapse,
  useDisclosure,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
} from '@chakra-ui/react';
import AdminDrawerContent from '../layout/AdminDrawerContent';
import {
  compactFormLabelProps,
  compactInputProps,
  compactSelectProps,
} from '../../theme/adminUi';
import { getData } from '../../utils/httpClient';
import { fetchVehicleCatalog } from '../../utils/vehicleCatalogCache';

const VehiclesEditModal = ({
  isOpen,
  onClose,
  vehicle,
  isNew,
  onCreate,
  onUpdate,
}) => {
  const [formData, setFormData] = React.useState({});
  const [errors, setErrors] = React.useState({});
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const allModelsRef = React.useRef([]);
  const [brandsList, setBrandsList] = React.useState([]);
  const [modelsList, setModelsList] = React.useState([]);
  const [colorList, setColorList] = React.useState([]);
  const [fuelTypeList, setFuelTypeList] = React.useState([]);
  const [customersList, setCustomersList] = React.useState([]);
  const [filteredCustomers, setFilteredCustomers] = React.useState([]);
  const [customerSearchQuery, setCustomerSearchQuery] = React.useState('');
  const [selectedCustomerName, setSelectedCustomerName] = React.useState('');
  const { isOpen: isCustomerListOpen, onToggle: onCustomerListToggle, onClose: onCustomerListClose } = useDisclosure();
  const [confirmModalState, setConfirmModalState] = React.useState({ isOpen: false });
  const [, setMissingFields] = React.useState([]);

  const toast = useToast();

  let inputColor = useColorModeValue('black', 'gray.400');
  const fieldProps = { ...compactInputProps, color: inputColor };
  const selectProps = { ...compactSelectProps, color: inputColor };

  // Función para validar solo los campos obligatorios
  const validateRequiredFields = () => {
    const newErrors = {};

    // Solo matrícula, marca y modelo son obligatorios
    if (!formData.plate?.trim()) {
      newErrors.plate = 'La matrícula es obligatoria';
    } else if (formData.plate.length < 2) {
      newErrors.plate = 'La matrícula debe tener al menos 2 caracteres';
    }

    if (!formData.brandId || formData.brandId === '0') {
      newErrors.brandId = 'Debe seleccionar una marca';
    }

    if (!formData.modelId || formData.modelId === '0') {
      newErrors.modelId = 'Debe seleccionar un modelo';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Función para verificar campos opcionales incompletos
  const checkIncompleteFields = () => {
    const incomplete = [];

    if (!formData.chassisNumber || formData.chassisNumber.trim() === '') {
      incomplete.push('Número de chasis');
    }
    if (!formData.engineNumber || formData.engineNumber.trim() === '') {
      incomplete.push('Número de motor');
    }
    if (!formData.year || formData.year === '0' || formData.year === 0) {
      incomplete.push('Año');
    }
    if (!formData.colorId || formData.colorId === '0' || formData.colorId === 0) {
      incomplete.push('Color');
    }
    if (!formData.fuelTypeId || formData.fuelTypeId === '0' || formData.fuelTypeId === 0) {
      incomplete.push('Tipo de combustible');
    }
    if (!formData.currentCustomerId || formData.currentCustomerId === '0' || formData.currentCustomerId === 0) {
      incomplete.push('Cliente');
    }

    return incomplete;
  };

  // Función para limpiar error de un campo específico
  const clearFieldError = (fieldName) => {
    if (errors[fieldName]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[fieldName];
        return newErrors;
      });
    }
  };

  // Get data from API

  const volkswagenBrandId = (brands) => {
    const brand = (brands || []).find(
      (item) => String(item?.name || '').trim().toLowerCase() === 'volkswagen',
    );
    return brand ? String(brand.id) : null;
  };

  const sortByLabel = (items, getLabel) =>
    [...(items || [])].sort((a, b) =>
      String(getLabel(a) || '').localeCompare(String(getLabel(b) || ''), 'es', {
        sensitivity: 'base',
      }),
    );

  const sortModelsByDescription = (models) =>
    sortByLabel(models, (m) => m?.description);

  const applyModelsForBrand = (brandId = null) => {
    const all = allModelsRef.current;
    if (!brandId || brandId === '0') {
      setModelsList(sortModelsByDescription(all));
      return;
    }
    const id = Number(brandId);
    setModelsList(
      sortModelsByDescription(
        all.filter(
          (m) =>
            Number(m.brandId) === id ||
            Number(m.brand?.id) === id,
        ),
      ),
    );
  };

  const loadCatalog = async () => {
    try {
      const catalog = await fetchVehicleCatalog(getData);
      allModelsRef.current = sortModelsByDescription(catalog.models);
      setBrandsList(sortByLabel(catalog.brands, (b) => b?.name));
      setColorList(sortByLabel(catalog.colors, (c) => c?.name));
      setFuelTypeList(sortByLabel(catalog.fuelTypes, (f) => f?.type));
      setCustomersList(catalog.customers);
      const existingBrandId =
        vehicle?.model?.brandId ?? vehicle?.model?.brand?.id ?? null;
      const defaultBrandId = isNew ? volkswagenBrandId(catalog.brands) : null;
      const brandId = existingBrandId || defaultBrandId || formData.brandId;
      if (isNew && defaultBrandId && !existingBrandId) {
        setFormData((prev) => {
          if (prev.brandId && String(prev.brandId) !== '0') return prev;
          return { ...prev, brandId: defaultBrandId };
        });
      }
      applyModelsForBrand(brandId);
    } catch (error) {
      allModelsRef.current = [];
      setBrandsList([]);
      setModelsList([]);
      setColorList([]);
      setFuelTypeList([]);
      setCustomersList([]);
      toast({
        title: 'Error al cargar catálogos',
        description: error.message || 'Ocurrió un error inesperado.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  useEffect(() => {
    if (!isOpen) return;
    loadCatalog();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  useEffect(() => {
    const term = customerSearchQuery.trim();
    if (term === '') {
      setFilteredCustomers([]);
      return undefined;
    }
    let cancelled = false;
    const timer = setTimeout(async () => {
      try {
        const params = new URLSearchParams({ page: '1', limit: '20', data: term });
        const response = await getData(`/customers?${params.toString()}`, {
          skipLoader: true,
        });
        if (!cancelled) {
          setFilteredCustomers(response?.payload?.customers || []);
        }
      } catch {
        if (!cancelled) setFilteredCustomers([]);
      }
    }, 300);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [customerSearchQuery]);

  useEffect(() => {
    const existingBrandId = vehicle?.model?.brandId ?? vehicle?.model?.brand?.id;
    const defaultBrandId =
      isNew && !existingBrandId ? volkswagenBrandId(brandsList) : null;
    const newFormData = {
      id: vehicle?.id ?? null,
      plate: vehicle?.plate?.toUpperCase() ?? '',
      chassisNumber: vehicle?.chassisNumber?.toUpperCase() ?? '',
      engineNumber: vehicle?.engineNumber?.toUpperCase() ?? '',
      modelId: vehicle?.modelId ?? vehicle?.model?.id ?? '0',
      brandId: existingBrandId ?? defaultBrandId ?? '0',
      year: vehicle?.year ? String(vehicle.year) : '0',
      currentCustomerId: vehicle?.currentCustomerId ?? vehicle?.currentCustomer?.id ?? '0',
      colorId: vehicle?.colorId ? String(vehicle.colorId) : '0',
      fuelTypeId: vehicle?.fuelTypeId ? String(vehicle.fuelTypeId) : '0',
      representative: vehicle?.representative ?? '',
      representativeContact: vehicle?.representativeContact ?? '',
      saleDate: vehicle?.saleDate ? new Date(vehicle.saleDate).toISOString().split('T')[0] : '',
      warrantyEndDate: vehicle?.warrantyEndDate ? new Date(vehicle.warrantyEndDate).toISOString().split('T')[0] : '',
    };
    
    setFormData(newFormData);

    const brandId =
      vehicle?.model?.brandId ??
      vehicle?.model?.brand?.id ??
      newFormData.brandId;
    if (allModelsRef.current.length > 0) {
      applyModelsForBrand(brandId);
    }

  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vehicle, isNew]);

  // Efecto separado para inicializar el cliente seleccionado cuando la lista de clientes se carga
  useEffect(() => {
    const customerId = vehicle?.currentCustomerId ?? vehicle?.currentCustomer?.id;
    
    if (customerId) {
      const selectedCustomer =
        customersList.find((customer) => customer.id === customerId) ||
        (vehicle?.currentCustomer?.id === customerId ? vehicle.currentCustomer : null);
      
      if (selectedCustomer) {
        const customerName = `${selectedCustomer.name || ''} ${selectedCustomer.lastName || selectedCustomer.surname || ''}`.trim();
        const customerDocument = selectedCustomer.document || selectedCustomer.documentNumber || '';
        setSelectedCustomerName(`${customerName} - ${customerDocument}`);
        setCustomerSearchQuery('');
      } else {
        setSelectedCustomerName('');
        setCustomerSearchQuery('');
      }
    } else {
      setSelectedCustomerName('');
      setCustomerSearchQuery('');
    }
  }, [vehicle, customersList]); // Ejecutar cuando cambie el vehículo o la lista de clientes

  // Limpiar errores cuando se cierre el modal
  useEffect(() => {
    if (!isOpen) {
      setErrors({});
      setIsSubmitting(false);
    }
  }, [isOpen]);

  const closeConfirmModal = () => {
    setConfirmModalState({ isOpen: false });
  };

  const handleConfirmContinue = () => {
    closeConfirmModal();
    handleSave(true);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    // Limpiar error del campo cuando el usuario empiece a escribir
    clearFieldError(name);

    // Convertir matrícula, chasis y número de motor a mayúsculas
    let formattedValue;
    if (name === 'chassisNumber' || name === 'plate' || name === 'engineNumber') {
      formattedValue = value.toUpperCase();
    } else if (name === 'year') {
      // Para el año, permitir solo números válidos
      if (value === '' || value === '0') {
        formattedValue = '0';
      } else {
        const numValue = Number(value);
        formattedValue = isNaN(numValue) ? '0' : String(numValue);
      }
    } else if (name === 'currentCustomerId' || name === 'colorId' || name === 'fuelTypeId' || name === 'brandId' || name === 'modelId') {
      // Para IDs, asegurar que sea un número válido
      const numValue = Number(value);
      formattedValue = isNaN(numValue) ? '0' : String(numValue);
    } else {
      formattedValue = value;
    }

    if (name === 'brandId') {
      applyModelsForBrand(formattedValue);
      setFormData({
        ...formData,
        brandId: formattedValue,
        modelId: '0',
      });
      clearFieldError('modelId');
      return;
    }

    setFormData({
      ...formData,
      [name]: formattedValue,
    });
  };

  const handleCustomerSearch = (e) => {
    const query = e.target.value;
    setCustomerSearchQuery(query);
    setSelectedCustomerName('');
    setFormData({ ...formData, currentCustomerId: 0 });
    
    if (query.trim() !== '' && !isCustomerListOpen) {
      onCustomerListToggle();
    } else if (query.trim() === '' && isCustomerListOpen) {
      onCustomerListClose();
    }
  };

  const handleCustomerSelect = (customer) => {
    const customerName = `${customer.name || ''} ${customer.lastName || customer.surname || ''}`.trim();
    const customerDocument = customer.document || customer.documentNumber || '';
    setSelectedCustomerName(`${customerName} - ${customerDocument}`);
    setCustomerSearchQuery('');
    setFormData({ ...formData, currentCustomerId: customer.id, representative: '', representativeContact: '' });
    clearFieldError('currentCustomerId');
    onCustomerListClose();
  };

  const clearCustomerSelection = () => {
    setSelectedCustomerName('');
    setCustomerSearchQuery('');
    setFormData({ ...formData, currentCustomerId: 0, representative: '', representativeContact: '' });
    onCustomerListClose();
  };
  

  const handleSave = async (forceSubmit = false) => {
    // Validar solo campos obligatorios (matrícula, marca y modelo)
    if (!validateRequiredFields()) {
      toast({
        title: 'Campos obligatorios incompletos',
        description: 'Por favor, complete la matrícula, marca y modelo.',
        status: 'error',
        duration: 4000,
        isClosable: true,
      });
      return;
    }

    // Si no se forzó el envío, verificar campos opcionales incompletos
    if (!forceSubmit) {
      const incomplete = checkIncompleteFields();
      if (incomplete.length > 0) {
        setMissingFields(incomplete);
        setConfirmModalState({ isOpen: true });
        return;
      }
    }

    // Verificar que las funciones necesarias estén disponibles
    if (isNew && !onCreate) {
      console.error('Función onCreate no está definida');
      toast({
        title: 'Error de configuración',
        description: 'No se puede crear el vehículo. Función onCreate no definida.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
      return;
    }

    if (!isNew && !onUpdate) {
      console.error('Función onUpdate no está definida');
      toast({
        title: 'Error de configuración',
        description: 'No se puede actualizar el vehículo. Función onUpdate no definida.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
      return;
    }

    setIsSubmitting(true);
    
    try {
      // Preparar datos para envío (convertir según lo esperado por la entidad)
      const dataToSend = {
        ...formData,
        kilometers: vehicle?.kilometers || 0, // Mantener el valor original de kilómetros
        year: formData.year || null, // year es VARCHAR en la BD
        modelId: Number(formData.modelId) || null,
        colorId: Number(formData.colorId) || null,
        fuelTypeId: Number(formData.fuelTypeId) || null,
        currentCustomerId: Number(formData.currentCustomerId) || null,
        saleDate: formData.saleDate || null,
        warrantyEndDate: formData.warrantyEndDate || null,
        // Remover brandId ya que no es parte de la entidad
        // El brandId se maneja a través de la relación model.brand
      };
      
      // Limpiar valores que son 0 y convertirlos a null para campos opcionales
      if (dataToSend.modelId === 0) dataToSend.modelId = null;
      if (dataToSend.colorId === 0) dataToSend.colorId = null;
      if (dataToSend.fuelTypeId === 0) dataToSend.fuelTypeId = null;
      if (dataToSend.currentCustomerId === 0) dataToSend.currentCustomerId = null;
      if (!dataToSend.year || dataToSend.year === '0') dataToSend.year = null;
      
      // Limpiar campos opcionales vacíos (chassis y motor pueden estar vacíos)
      if (!dataToSend.chassisNumber?.trim()) dataToSend.chassisNumber = null;
      if (!dataToSend.engineNumber?.trim()) dataToSend.engineNumber = null;
      if (!dataToSend.representative?.trim()) dataToSend.representative = null;
      if (!dataToSend.representativeContact?.trim()) dataToSend.representativeContact = null;
      
      // Remover brandId del objeto a enviar
      delete dataToSend.brandId;
      
    // Validación solo de matrícula (único campo de texto obligatorio)
    if (!dataToSend.plate?.trim()) {
      throw new Error('La matrícula es obligatoria');
    }
    
    if (isNew) {
      await onCreate(dataToSend);
    } else {
      await onUpdate(dataToSend);
    }
    
    toast({
      title: isNew ? 'Vehículo creado.' : 'Vehículo actualizado.',
      status: 'success',
      duration: 3000,
      isClosable: true,
    });
    setErrors({}); // Limpiar errores
    onClose(); // Solo cerrar si la operación fue exitosa
    } catch (error) {
      console.error('Error al guardar vehículo:', error);
      
      // Manejar errores específicos de duplicación
      const errorMessage = error.message || 'Ocurrió un error inesperado.';
      
      let title = 'Error';
      let description = errorMessage;
      
      // Verificar si es un error de duplicación de matrícula o chasis
      if (errorMessage.toLowerCase().includes('plate') || errorMessage.toLowerCase().includes('matrícula')) {
        title = 'Matrícula duplicada';
        description = 'Ya existe un vehículo con esta matrícula.';
        setErrors(prev => ({ ...prev, plate: 'Esta matrícula ya está en uso' }));
      } else if (errorMessage.toLowerCase().includes('chassis') || errorMessage.toLowerCase().includes('chasis')) {
        title = 'Chasis duplicado';
        description = 'Ya existe un vehículo con este número de chasis.';
        setErrors(prev => ({ ...prev, chassisNumber: 'Este número de chasis ya está en uso' }));
      } else if (errorMessage.toLowerCase().includes('engine') || errorMessage.toLowerCase().includes('motor')) {
        title = 'Motor duplicado';
        description = 'Ya existe un vehículo con este número de motor.';
        setErrors(prev => ({ ...prev, engineNumber: 'Este número de motor ya está en uso' }));
      }
      
      toast({
        title,
        description,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
      // NO cerrar el modal cuando hay errores
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
    <Drawer isOpen={isOpen} placement="right" onClose={onClose} closeOnOverlayClick={false}>
      <DrawerOverlay />
      <AdminDrawerContent>
        <DrawerCloseButton />
        <DrawerHeader fontSize="lg" fontWeight="bold" textAlign="center">
          {isNew ? 'Nuevo vehículo' : 'Editar vehículo'}
        </DrawerHeader>
        <DrawerBody pb={6}>
          <VStack spacing={4} align="stretch">
            <SimpleGrid columns={{ base: 1, sm: 2, lg: 4 }} spacing={3}>
              <FormControl isInvalid={!!errors.plate}>
                <FormLabel {...compactFormLabelProps}>Matrícula *</FormLabel>
                <Input
                  name="plate"
                  value={formData.plate}
                  onChange={handleChange}
                  placeholder="Matrícula"
                  borderColor={errors.plate ? 'red.300' : 'gray.300'}
                  {...fieldProps}
                />
                <FormErrorMessage>{errors.plate}</FormErrorMessage>
              </FormControl>

              <FormControl>
                <FormLabel {...compactFormLabelProps}>Chasis</FormLabel>
                <Input
                  name="chassisNumber"
                  value={formData.chassisNumber}
                  onChange={handleChange}
                  placeholder="Nº chasis"
                  {...fieldProps}
                />
              </FormControl>

              <FormControl>
                <FormLabel {...compactFormLabelProps}>Motor</FormLabel>
                <Input
                  name="engineNumber"
                  value={formData.engineNumber}
                  onChange={handleChange}
                  placeholder="Nº motor"
                  {...fieldProps}
                />
              </FormControl>

              <FormControl>
                <FormLabel {...compactFormLabelProps}>Año</FormLabel>
                <Input
                  name="year"
                  type="number"
                  value={formData.year}
                  onChange={handleChange}
                  placeholder="Año"
                  {...fieldProps}
                />
              </FormControl>
            </SimpleGrid>

            <SimpleGrid columns={{ base: 1, sm: 2, lg: 4 }} spacing={3}>
              <FormControl isInvalid={!!errors.brandId}>
                <FormLabel {...compactFormLabelProps}>Marca *</FormLabel>
                <Select
                  name="brandId"
                  value={formData.brandId}
                  onChange={handleChange}
                  borderColor={errors.brandId ? 'red.300' : 'gray.300'}
                  {...selectProps}
                >
                  <option value={0} disabled>
                    Seleccionar marca
                  </option>
                  {brandsList?.map((brand) => (
                    <option key={brand.id} value={brand.id}>
                      {brand.name}
                    </option>
                  ))}
                </Select>
                <FormErrorMessage>{errors.brandId}</FormErrorMessage>
              </FormControl>

              <FormControl isInvalid={!!errors.modelId}>
                <FormLabel {...compactFormLabelProps}>Modelo *</FormLabel>
                <Select
                  name="modelId"
                  value={formData.modelId}
                  onChange={handleChange}
                  borderColor={errors.modelId ? 'red.300' : 'gray.300'}
                  disabled={!formData.brandId || formData.brandId === '0'}
                  {...selectProps}
                >
                  <option value={0} disabled>
                    {!formData.brandId || formData.brandId === '0'
                      ? 'Seleccione marca'
                      : 'Seleccionar modelo'}
                  </option>
                  {modelsList?.map((model) => (
                    <option key={model.id} value={model.id}>
                      {model.description}
                    </option>
                  ))}
                </Select>
                <FormErrorMessage>{errors.modelId}</FormErrorMessage>
              </FormControl>

              <FormControl>
                <FormLabel {...compactFormLabelProps}>Color</FormLabel>
                <Select
                  name="colorId"
                  value={formData.colorId}
                  onChange={handleChange}
                  {...selectProps}
                >
                  <option value={0} disabled>
                    Seleccionar color
                  </option>
                  {colorList?.map((color) => (
                    <option key={color.id} value={color.id}>
                      {color.name}
                    </option>
                  ))}
                </Select>
              </FormControl>

              <FormControl>
                <FormLabel {...compactFormLabelProps}>Combustible</FormLabel>
                <Select
                  name="fuelTypeId"
                  value={formData.fuelTypeId}
                  onChange={handleChange}
                  {...selectProps}
                >
                  <option value={0} disabled>
                    Seleccionar tipo
                  </option>
                  {fuelTypeList?.map((fuelType) => (
                    <option key={fuelType.id} value={fuelType.id}>
                      {fuelType.type}
                    </option>
                  ))}
                </Select>
              </FormControl>
            </SimpleGrid>

            <FormControl>
              <FormLabel {...compactFormLabelProps}>Cliente</FormLabel>
              <Box position="relative" maxW={{ base: '100%', lg: '420px' }}>
                <Input
                  placeholder="Buscar por nombre o documento..."
                  value={selectedCustomerName || customerSearchQuery}
                  onChange={handleCustomerSearch}
                  autoComplete="off"
                  {...fieldProps}
                />
                {selectedCustomerName && (
                  <Button
                    size="xs"
                    position="absolute"
                    right="2"
                    top="50%"
                    transform="translateY(-50%)"
                    variant="ghost"
                    onClick={clearCustomerSelection}
                    color="red.500"
                    _hover={{ bg: 'red.50' }}
                  >
                    ✕
                  </Button>
                )}
                <Collapse in={isCustomerListOpen && filteredCustomers.length > 0}>
                  <Box
                    position="absolute"
                    top="100%"
                    left="0"
                    right="0"
                    zIndex="1000"
                    bg="white"
                    border="1px solid"
                    borderColor="gray.200"
                    borderRadius="md"
                    maxH="200px"
                    overflowY="auto"
                    mt="1"
                    boxShadow="lg"
                  >
                    <List spacing={0}>
                      {filteredCustomers.map((customer) => (
                        <ListItem
                          key={customer.id}
                          p="3"
                          cursor="pointer"
                          _hover={{ bg: 'blue.50' }}
                          borderBottom="1px solid"
                          borderColor="gray.100"
                          onClick={() => handleCustomerSelect(customer)}
                        >
                          <Text fontWeight="medium" color="gray.800" fontSize="sm">
                            {customer.name} {customer.lastName || customer.surname || ''}
                          </Text>
                          <Text fontSize="xs" color="gray.600">
                            {customer.document || customer.documentNumber || 'Sin documento'}
                          </Text>
                        </ListItem>
                      ))}
                    </List>
                  </Box>
                </Collapse>
              </Box>
            </FormControl>

            <SimpleGrid columns={{ base: 1, sm: 2, lg: 4 }} spacing={3}>
              <FormControl>
                <FormLabel {...compactFormLabelProps}>Fecha de venta</FormLabel>
                <Input
                  name="saleDate"
                  type="date"
                  value={formData.saleDate}
                  onChange={handleChange}
                  {...fieldProps}
                />
              </FormControl>

              <FormControl>
                <FormLabel {...compactFormLabelProps}>Fin de garantía</FormLabel>
                <Input
                  name="warrantyEndDate"
                  type="date"
                  value={formData.warrantyEndDate}
                  onChange={handleChange}
                  {...fieldProps}
                />
              </FormControl>

              <FormControl>
                <FormLabel {...compactFormLabelProps}>Representante</FormLabel>
                <Input
                  name="representative"
                  value={formData.representative}
                  onChange={handleChange}
                  placeholder="Nombre"
                  {...fieldProps}
                />
              </FormControl>

              <FormControl>
                <FormLabel {...compactFormLabelProps}>Contacto rep.</FormLabel>
                <Input
                  name="representativeContact"
                  value={formData.representativeContact}
                  onChange={handleChange}
                  placeholder="Tel. o email"
                  {...fieldProps}
                />
              </FormControl>
            </SimpleGrid>
          </VStack>
        </DrawerBody>
        <HStack justify="flex-end" p={4}>
          <Button 
            colorScheme="gray" 
            onClick={() => handleSave(false)}
            isLoading={isSubmitting}
            loadingText="Guardando..."
            disabled={isSubmitting}
          >
            Guardar
          </Button>
          <Button 
            variant="outline" 
            onClick={onClose}
            disabled={isSubmitting}
          >
            Cancelar
          </Button>
        </HStack>
      </AdminDrawerContent>
    </Drawer>

    {/* Modal de confirmación debe estar fuera del Drawer */}
    <Modal isOpen={confirmModalState.isOpen} onClose={closeConfirmModal} closeOnOverlayClick={false}>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Campos incompletos</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          ¿Desea continuar de todas formas?
        </ModalBody>
        <ModalFooter>
          <Button colorScheme="gray" mr={3} onClick={closeConfirmModal}>
            Cancelar
          </Button>
          <Button variant="adminPrimary" onClick={handleConfirmContinue}>
            Continuar
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  </>
  );
};

export default VehiclesEditModal;
